import { ChevronDownIcon } from "lucide-react";
import { useId, useState } from "react";
import { type Control, type FieldPath, useController, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Checkbox } from "../../../../../components/ui/checkbox";
import { Input } from "../../../../../components/ui/input";
import { cn } from "../../../../../lib/utils";
import { ACCOUNT_TYPE_OPTIONS, BANK_NAME_MAX_LENGTH, BANK_NAME_REGEX } from "../constants";
import type { BankAccountInput, SignersInput } from "../Signers.schema";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type PersonPath = "driver" | "secondSigner";

interface BankInformationProperties {
  control: Control<SignersInput>;
  personPath: PersonPath;
  /** Ref callback registering this subsection with the Signers-level scroll-spy. */
  sectionRef?: (element: HTMLElement | null) => (() => void) | void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function getBankFieldPath(personPath: PersonPath, field: keyof BankAccountInput): FieldPath<SignersInput> {
  return `${personPath}.bankAccount.${field}` as FieldPath<SignersInput>;
}

// ---------------------------------------------------------------------------
// BankInformation
//
// Collapsible per-person subsection with three independent account-type
// checkboxes and a conditional Bank Name input (shown when ≥1 type is checked).
// Collapse state is block-local (UI concern only — mirrors IncomeBlock pattern).
// ---------------------------------------------------------------------------
export function BankInformation({ control, personPath, sectionRef }: BankInformationProperties) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);
  // Unique id per mount — prevents aria-controls collisions when both driver
  // and secondSigner columns are rendered in the same document.
  const uid = useId();
  const contentId = `${personPath}-bank-info-${uid}-content`;

  // Watch the three boolean paths so Bank Name visibility is reactive.
  const isSavingsAccount = useWatch({ control, name: getBankFieldPath(personPath, "isSavingsAccount") });
  const isCheckingAccount = useWatch({ control, name: getBankFieldPath(personPath, "isCheckingAccount") });
  const isPrepaidDebit = useWatch({ control, name: getBankFieldPath(personPath, "isPrepaidDebit") });
  const anyAccountTypeSelected = Boolean(isSavingsAccount) || Boolean(isCheckingAccount) || Boolean(isPrepaidDebit);

  const bankNamePath = getBankFieldPath(personPath, "bankName");
  const { field: bankNameField, fieldState: bankNameFieldState } = useController({
    control,
    name: bankNamePath,
  });
  // DOM ids derive from the per-mount uid (not the RHF path) so they stay unique
  // even if this subsection were ever mounted twice for the same personPath —
  // consistent with the useId()-backed contentId above.
  const bankNameInputId = `${uid}-bank-name`;
  const bankNameErrorId = `${bankNameInputId}-error`;

  function handleToggle() {
    setIsOpen((previous) => !previous);
  }

  function handleBankNameChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;
    // Reject input containing characters outside the allowed set (mirrors
    // dealer-ui BANK_NAME_REGEX gate). The field simply does not update.
    if (!BANK_NAME_REGEX.test(value)) {
      return;
    }
    bankNameField.onChange(value);
  }

  function handleBankNameBlur() {
    // Trim whitespace and uppercase on blur — matches dealer-ui behavior.
    const trimmedUpper = (bankNameField.value as string | undefined)?.trim().toUpperCase() ?? "";
    // Only push an update when the value actually changes, to avoid a redundant
    // RHF onChange/re-validation on every blur of an already-normalized field.
    if (trimmedUpper !== bankNameField.value) {
      bankNameField.onChange(trimmedUpper);
    }
    bankNameField.onBlur();
  }

  // Called by AccountTypeCheckbox when its unchecked toggle would leave all
  // three account types un-selected, making Bank Name hidden. Clearing here
  // (rather than inside the child) avoids stale-closure issues and keeps the
  // "watched sibling values" logic in one place.
  function clearBankNameIfBecomingHidden(fieldBeingToggled: keyof BankAccountInput) {
    // Compute which OTHER fields are currently checked.
    const otherValues: Record<keyof BankAccountInput, boolean | undefined> = {
      isSavingsAccount: Boolean(isSavingsAccount),
      isCheckingAccount: Boolean(isCheckingAccount),
      isPrepaidDebit: Boolean(isPrepaidDebit),
      bankName: undefined,
    };
    // After this toggle the target field goes false; check if all others are already false.
    const othersChecked = ACCOUNT_TYPE_OPTIONS.filter((o) => o.field !== fieldBeingToggled).some(
      (o) => otherValues[o.field]
    );
    if (!othersChecked) {
      bankNameField.onChange("");
    }
  }

  return (
    <div ref={sectionRef}>
      {/* Section header */}
      <div className="bg-muted flex items-center gap-2 rounded-md px-3 py-2">
        <button
          type="button"
          aria-expanded={isOpen}
          aria-controls={contentId}
          onClick={handleToggle}
          className="flex flex-1 items-center justify-between gap-2 text-left text-sm font-medium"
        >
          <span>{t("review.bank.heading")}</span>
          <ChevronDownIcon
            className={cn(
              "text-muted-foreground size-4 shrink-0 transition-transform duration-200",
              isOpen ? "rotate-180" : "rotate-0"
            )}
            aria-hidden="true"
          />
        </button>
      </div>

      {/* Section content */}
      {isOpen && (
        <div id={contentId} className="flex flex-col gap-3 px-3 pb-4 pt-3">
          {/* Bank Name — only shown when ≥1 account type is checked */}
          {anyAccountTypeSelected && (
            <div className="flex flex-col gap-1">
              <label htmlFor={bankNameInputId} className="text-xs font-medium">
                {t("review.bank.bankName")}
              </label>
              <Input
                id={bankNameInputId}
                type="text"
                value={(bankNameField.value as string | undefined) ?? ""}
                onChange={handleBankNameChange}
                onBlur={handleBankNameBlur}
                maxLength={BANK_NAME_MAX_LENGTH}
                aria-describedby={bankNameFieldState.error ? bankNameErrorId : undefined}
                aria-invalid={bankNameFieldState.error ? true : undefined}
                className="w-full"
              />
              {bankNameFieldState.error && (
                <p id={bankNameErrorId} className="text-destructive text-2xs">
                  {bankNameFieldState.error.message}
                </p>
              )}
            </div>
          )}

          {/* Account-type checkboxes — independent multi-select, all optional.
              Laid out as a horizontal row (wraps on narrow widths) per design. */}
          <div className="flex flex-col gap-1" role="group" aria-label={t("review.bank.accountType.label")}>
            <span className="text-xs font-medium">{t("review.bank.accountType.label")}</span>
            <div className="flex flex-wrap gap-4">
              {ACCOUNT_TYPE_OPTIONS.map((option) => (
                <AccountTypeCheckbox
                  key={option.field}
                  control={control}
                  personPath={personPath}
                  option={option}
                  onClearBankName={clearBankNameIfBecomingHidden}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AccountTypeCheckbox — single account-type checkbox wired to RHF.
//
// When unchecked, calls onClearBankName (provided by parent) so the parent can
// decide whether all types are now unselected and clear the bankName value.
// ---------------------------------------------------------------------------
interface AccountTypeCheckboxProperties {
  control: Control<SignersInput>;
  personPath: PersonPath;
  option: (typeof ACCOUNT_TYPE_OPTIONS)[number];
  onClearBankName: (field: keyof BankAccountInput) => void;
}

function AccountTypeCheckbox({ control, personPath, option, onClearBankName }: AccountTypeCheckboxProperties) {
  const fieldPath = getBankFieldPath(personPath, option.field);
  const { field, fieldState } = useController({ control, name: fieldPath });
  // DOM id derives from a per-instance uid (not the RHF path) so checkbox/label
  // ids stay unique even across duplicate mounts — matches the panel's useId().
  const uid = useId();
  const checkboxId = `${uid}-${option.field}`;
  const errorId = `${checkboxId}-error`;

  function handleCheckedChange(checked: boolean | "indeterminate") {
    const isNowChecked = checked === true;
    field.onChange(isNowChecked);
    // When unchecking, notify the parent so it can clear bankName if this was
    // the last selected account type.
    if (!isNowChecked) {
      onClearBankName(option.field);
    }
  }

  return (
    <div className="flex items-center gap-2">
      <Checkbox
        id={checkboxId}
        checked={Boolean(field.value)}
        onCheckedChange={handleCheckedChange}
        aria-describedby={fieldState.error ? errorId : undefined}
        aria-invalid={fieldState.error ? true : undefined}
      />
      <label htmlFor={checkboxId} className="cursor-pointer text-xs font-normal">
        {option.label}
      </label>
      {fieldState.error && (
        <p id={errorId} className="text-destructive text-2xs">
          {fieldState.error.message}
        </p>
      )}
    </div>
  );
}
